import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { ACTIVE_SEVERITIES, severityDotClass, getSeverityConfig } from "@/config/severity";
import type { DefectSeverity } from "@/types";

export interface SeveritySelectorCardsProps {
  /** Currently selected severity. */
  value: DefectSeverity | null;
  /** Called with the chosen severity. */
  onChange: (value: DefectSeverity) => void;
  /** Disables selection. */
  disabled?: boolean;
  /** Render the screen-03 caption line under each label (e.g. "Sofortstopp"). */
  showCaptions?: boolean;
  className?: string;
}

/**
 * Der verbindliche A/B/C-Schweregrad-Katalog (TES-1692) als Auswahl-Karten: A=kritisch
 * (critical), B=Haupt (high, Default), C=Neben (medium) — über `ACTIVE_SEVERITIES` aus dem
 * Severity-SoT (`@/config/severity`). Der Enum-Wert `low` wird NICHT angeboten. Dot-Farben,
 * Katalog-Label/Caption-Keys und der `critical`-Pulse kommen ebenfalls aus dem SoT.
 */

/** Coloured status dot; the `critical` level pulses (screen-03 L191 `animate-pulse`). */
function SeverityDot({ severity }: { severity: DefectSeverity }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "h-3 w-3 rounded-full",
        severityDotClass[severity],
        getSeverityConfig(severity).pulse && "animate-pulse",
      )}
    />
  );
}

/** Optional caption line under the label (screen-03, e.g. "Sofortstopp"). */
function SeverityCaption({ severity }: { severity: DefectSeverity }) {
  const { t } = useTranslation();
  return (
    <span className="text-label-caps uppercase text-muted-foreground">
      {t(getSeverityConfig(severity).captionKey)}
    </span>
  );
}

interface SeverityCardProps {
  severity: DefectSeverity;
  selected: boolean;
  disabled: boolean;
  showCaptions: boolean;
  onSelect: (value: DefectSeverity) => void;
}

/** A single selectable severity card (extracted to keep the map callback flat). */
function SeverityCard({ severity, selected, disabled, showCaptions, onSelect }: SeverityCardProps) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      data-testid={`severity-card-${severity}`}
      data-state={selected ? "selected" : "unselected"}
      onClick={() => onSelect(severity)}
      className={cn(
        "group flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-outline-variant p-4 transition-all duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] hover:bg-surface-container-high active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50",
        selected && "border-primary bg-surface-container-high ring-2 ring-primary",
      )}
    >
      <SeverityDot severity={severity} />
      <span className="text-sm font-semibold text-foreground">
        {t(getSeverityConfig(severity).catalogLabelKey)}
      </span>
      {showCaptions && <SeverityCaption severity={severity} />}
    </button>
  );
}

/**
 * A/B/C severity selector cards (Stitch "Industrial Management Portal", screen-03 L178-194).
 *
 * Renders the binding A/B/C catalog levels (ACTIVE_SEVERITIES: critical, high, medium) as
 * selectable cards; `low` is not offered (TES-1692). The selected card gets
 * `ring-2 ring-primary bg-surface-container-high border-primary`; each card shows a coloured
 * `severity-*` dot + the A/B/C catalog label via the defects.severity.catalog.* i18n keys.
 */
export function SeveritySelectorCards({
  value,
  onChange,
  disabled = false,
  showCaptions = false,
  className,
}: SeveritySelectorCardsProps) {
  const { t } = useTranslation();

  return (
    <div
      role="radiogroup"
      aria-label={t("defects.severity.label")}
      data-testid="severity-selector-cards"
      className={cn("grid grid-cols-1 gap-3 sm:grid-cols-3", className)}
    >
      {ACTIVE_SEVERITIES.map((severity) => (
        <SeverityCard
          key={severity}
          severity={severity}
          selected={value === severity}
          disabled={disabled}
          showCaptions={showCaptions}
          onSelect={onChange}
        />
      ))}
    </div>
  );
}
